import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, AlertCircle } from 'lucide-react';
import { api, formatDate, formatMoney } from '../../lib/api';

export default function AdminReferrals() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api('/api/admin/referrals')
    .then(setData)
    .catch((e) => setError(e.message || 'Failed to load referrals.'));

  useEffect(() => { load(); }, []);

  const act = async (creditId, action) => {
    if (action === 'revoke' && !window.confirm('Revoke this unused credit? Use this when the qualifying purchase was refunded.')) return;
    setBusyId(creditId);
    setError(null);
    try {
      await api(`/api/admin/referrals/credits/${creditId}/${action}`, { method: 'POST' });
      await load();
    } catch (e) {
      setError(e.message || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const t = data?.totals;

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-2">
        <Gift size={22} /> Referrals
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        A new customer who uses someone's code gets 10% off their first paid course. When they pay, the referrer earns a 10% credit.
        One credit per course; credits never stack with each other or with a discount code.
      </p>

      {error && (
        <div className="mb-4 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded text-sm flex items-start gap-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card label="Referrals" value={t.referrals} />
          <Card label="Became buyers" value={t.qualified} />
          <Card label="Unused credits" value={t.credits_available} />
          <Card label="Discounts given" value={formatMoney(t.discount_given_cents)} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <Th>Referrer</Th>
                <Th>New customer</Th>
                <Th>Code / where</Th>
                <Th>Status</Th>
                <Th>New customer's credit</Th>
                <Th>Referrer's credit</Th>
              </tr>
            </thead>
            <tbody>
              {!data ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
              ) : !data.referrals.length ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No referrals yet.</td></tr>
              ) : data.referrals.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top">
                  <Td><Person id={r.referrer_id} name={r.referrer_name} email={r.referrer_email} /></Td>
                  <Td><Person id={r.referee_id} name={r.referee_name} email={r.referee_email} /></Td>
                  <Td>
                    <div className="font-mono text-xs whitespace-nowrap">{r.code_used}</div>
                    <div className="text-xs text-slate-500">{r.source === 'signup' ? 'At sign-up' : 'At checkout'} · {formatDate(r.created_at)}</div>
                  </Td>
                  <Td>
                    {r.status === 'qualified' ? (
                      <>
                        <span className="text-emerald-700 font-semibold">Bought</span>
                        <div className="text-xs text-slate-500">
                          {r.qualifying_course || 'Course'} · {formatMoney(r.qualifying_amount_cents || 0)}
                          {r.qualifying_purchase_status === 'refunded' && <span className="text-rose-600"> · refunded</span>}
                        </div>
                      </>
                    ) : (
                      <span className="text-slate-500">Not bought yet</span>
                    )}
                  </Td>
                  <Td><Credit credit={r.referee_credit} busy={busyId} onAct={act} /></Td>
                  <Td><Credit credit={r.referrer_credit} busy={busyId} onAct={act} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Credit({ credit, busy, onAct }) {
  if (!credit) return <span className="text-slate-400">—</span>;
  const label = { available: 'Unused', used: 'Used', revoked: 'Revoked' }[credit.status] || credit.status;
  const color = { available: 'text-emerald-700', used: 'text-slate-600', revoked: 'text-rose-600' }[credit.status] || '';
  return (
    <div>
      <span className={`font-semibold ${color}`}>{credit.percent}% · {label}</span>
      {credit.status === 'used' && <div className="text-xs text-slate-500">{formatDate(credit.used_at)}</div>}
      {credit.status === 'available' && (
        <button
          type="button"
          disabled={busy === credit.id}
          onClick={() => onAct(credit.id, 'revoke')}
          className="block text-xs text-rose-700 hover:underline disabled:opacity-50"
        >
          Revoke
        </button>
      )}
      {credit.status === 'revoked' && (
        <button
          type="button"
          disabled={busy === credit.id}
          onClick={() => onAct(credit.id, 'restore')}
          className="block text-xs text-slate-700 hover:underline disabled:opacity-50"
        >
          Restore
        </button>
      )}
    </div>
  );
}

function Person({ id, name, email }) {
  return (
    <div>
      <Link to={`/admin/users/${id}`} className="font-medium text-slate-900 hover:underline">{name || email}</Link>
      <div className="text-xs text-slate-500">{email}</div>
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{children}</th>;
}

function Td({ children }) {
  return <td className="px-4 py-3">{children}</td>;
}
